import React, { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';

import PriceBlock from '../../common/PriceBlock';
import PhysicalSetBlock from '../../common/PhysicalSetBlock';

import TopBlock from '../TopBlock';
import ImagesBlock from '../ImagesBlock';
import PropertiesBlock from '../PropertiesBlock';
import ParametersBlock from '../ParametersBlock';
import VariantsBlock from '../VariantsBlock';
import SubscriptionBlock from '../SubscriptionBlock';
import AdAttachmentsParametersBlock from '../AdAttachmentsParametersBlock';

import Button from '../../../common/Button';
import PageTitle from '../../../common/PageTitle';

import { preventSubmittingByEnter } from '../../../../helpers';
import PriceBlockWithAutoCalc from '../../common/PriceBlockWithAutoCalc';
import VirtualSetBlock from '../../common/VirtualSetBlock';
import ProductChangesBlock from '../ProductChangesBlock';
import MarginSalesBlock from '../../../Settings/MarginSalesBlock';
import MarginSalesChart from '../../../Summary/MarginSalesChart';
import ProductsPriceMarginHistory from '../../ProductsPriceMarginHistory';
import MonoboxesBlock from '../../common/MonoboxesBlock';
import { useAutoTranslation } from '../../../../helpers/useAutoTranslation';

const FormWrapper = styled('form')`
  position: relative;

  .invalid-feedback {
    display: block;
  }

  .card-box {
    margin-bottom: 25px;
    height: calc(100% - 25px);
    h4 {
      margin-bottom: 25px;
    }
  }
  .image-gallery {
    margin-top: 25px;
    img {
      max-width: 125px;
      margin: 10px;
    }
  }
  .option-row {
    align-items: center;
    border-top: 1px solid #ccc;
    padding-top: 10px;
    label {
      margin-bottom: 15px;
    }
    margin: 10px 0 0 0;
    + .add-option-button-row {
      border-top: 1px solid #ccc;
      padding-top: 15px;
    }
  }
  .attribute-row {
    align-items: center;
    border-top: 1px solid #f7f6f6;
    padding-top: 10px;
    margin-top: 10px;
    margin-left: 1.5rem !important;
    margin-right: 1.5rem !important;
    > div {
      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        padding-right: 0;
      }
    }
    label {
      margin-bottom: 15px;
    }
    + .add-option-button-row {
      border-top: 1px solid #f7f6f6;
      padding-top: 15px;
      margin-left: 1.5rem !important;
      margin-right: 1.5rem !important;
    }
  }
  .remove-option {
    cursor: pointer;
    color: #4a81d4;
    &:hover {
      text-decoration: underline;
      color: #306cc8;
    }
  }
  .variants-table {
    overflow-x: auto;
    .variant-input {
      width: 110px;
    }
    td {
      vertical-align: middle;
      &.table-item-title {
        font-weight: bold;
        cursor: pointer;
      }
      &.table-item-image {
        cursor: pointer;
      }
    }
  }
  .add-option-button-row {
    margin: 10px 0 0 0;
  }
  .add-new-field {
    color: #7e57c2;
    cursor: pointer;
  }

  .product-id-top {
    margin-left: auto;
  }

  .variant-id-span,
  .variant-stock-span {
    font-weight: initial;
    display: block;
    font-size: 11px;
    color: #aaaaaa;
    cursor: pointer;
  }
  .variant-stock-span {
    &:hover {
      text-decoration: underline;
    }
  }

  .dropdown-menu {
    position: absolute;
    will-change: transform;
    bottom: 100%;
    top: unset;
    left: 0;
    transform: translate3d(0px, 35px, 0px);

    &.show {
      bottom: 100%;
      top: unset !important;
    }

    .dropdown-item {
      cursor: pointer;
      padding-left: 5px;
      padding-right: 5px;
    }
  }

  @media (max-width: 650px) {
    .option-row {
      label {
        margin-bottom: 0.5rem;
      }
      > div {
        width: 100%;
        max-width: 100%;
      }
      .sorting-block {
        order: 1;
        width: auto;
        padding: 0;
        button {
          padding-right: 0;
        }
      }
      .title-block {
        order: 2;
      }
      .removing-block {
        order: 3;
        margin-left: auto;
      }
      .tags-block {
        order: 4;
        margin-top: 10px;
        flex: 1 0 100%;
      }
    }
  }
`;

const DesktopLayout = (props) => {
  const {
    productId,
    pageTitle,
    pageTitleBadge,
    breadcrumbs,
    customBackLink,
    isNew,
    currentProduct,
    values,
    errors,
    handleChangeInput,
    images,
    uploadImages,
    reorderImages,
    zoomImage,
    handleDeleteImage,
    useVariants,
    attributes,
    additionalDescsOptions,
    addNewAttribute,
    handleSubmit,
    warehouses,
    handleChangeWeightType,
    loading,
    isSavedVariants,
    needSaving,
    features,
    variants,
    variantsValidation,
    selectedVariants,
    allSelectedVariants,
    appearanceFields,
    handleSelectVariant,
    handleAllSelectVariants,
    options,
    updateVariant,
    deleteVariant,
    deleteLoading,
    addNewFeature,
    dispatch,
    handleShowStockByBatch,
    handleVariantsUsing,
    handleNeedSaving,
    handleSetLastAction,
    handleAddManualVariant,
    setUnsavedStatus,
    openVariant,
    refreshArrearance,
    saveProduct,
    isArchived,
    handleCopy,
    allIsValid,
    skuSet,
    handleHideShow,
    handleArchiveItem,
    handleExportMovements,
    handleUpdateSkuSet,
    actualizePhysicalSet,
    moveVariant,
    handleCreateSkuSet,
    unSavedSkuSet,
    openItem,
    eshops,
    productMarginDelivered,
    productMarginShipped,
    isSingleProduct,
    isHaveOptionsUsed,
    productsPriceMarginHistory,
    haveProductsPriceMarginHistory,
    openImportChestnyznakModal,
    refreshProduct,
    monoboxesUpdateRef,
  } = props;

  const [showDropdown, setShowDropdown] = useState(false);

  const { t } = useAutoTranslation();

  return (
    <>
      <PageTitle
        title={t(pageTitle)}
        pageTitleBadge={pageTitleBadge}
        breadcrumbs={breadcrumbs}
        customBackLink={customBackLink}
      />
      <FormWrapper onKeyDown={preventSubmittingByEnter} onSubmit={handleSubmit}>
        <div className="row">
          <TopBlock
            isNew={isNew}
            currentProduct={currentProduct}
            values={values}
            errors={errors}
            handleChangeInput={handleChangeInput}
            type={values.type}
          />
          <ImagesBlock
            images={images}
            onUpload={uploadImages}
            onReorder={reorderImages}
            onItemClick={zoomImage}
            onDelete={handleDeleteImage}
            showHover
            copyImage
            showDefault
            defaultIndex={0}
            dropzoneDescription={t('Перетащите сюда файлы или кликните для их выбора')}
          />
        </div>

        {values.type !== 'SUBSCRIPTION' && values.type !== 'AD_ATTACHMENT' ? (
          <>
            {!useVariants ? (
              <>
                <div className="row">
                  <div className="col-lg-6">
                    {values.type === 'BUNDLE' ? (
                      <PriceBlockWithAutoCalc
                        currentProduct={currentProduct}
                        values={values}
                        handleChangeInput={handleChangeInput}
                      />
                    ) : (
                      <PriceBlock
                        currentProduct={currentProduct}
                        values={values}
                        handleChangeInput={handleChangeInput}
                      />
                    )}
                    <PropertiesBlock
                      values={values}
                      errors={errors}
                      attributes={attributes}
                      additionalDescsOptions={additionalDescsOptions}
                      handleShowStockByBatch={handleShowStockByBatch}
                      handleChangeInput={handleChangeInput}
                      addNewAttribute={addNewAttribute}
                      isNew={isNew}
                      currentProduct={currentProduct}
                    />
                  </div>
                  <ParametersBlock
                    currentProduct={currentProduct}
                    values={values}
                    warehouses={warehouses}
                    errors={errors}
                    handleChangeInput={handleChangeInput}
                    handleChangeWeightType={handleChangeWeightType}
                    handleSubmit={handleSubmit}
                    type={values.type}
                    eshops={eshops}
                  />
                </div>
              </>
            ) : (
              <div className="row">
                <div className="col-12">
                  <PropertiesBlock
                    values={values}
                    errors={errors}
                    attributes={attributes}
                    additionalDescsOptions={additionalDescsOptions}
                    handleShowStockByBatch={handleShowStockByBatch}
                    handleChangeInput={handleChangeInput}
                    addNewAttribute={addNewAttribute}
                    currentProduct={currentProduct}
                  />
                </div>
              </div>
            )}
            {values.type === 'PHYSICAL_SET' ? (
              <div className="row">
                <div className="col-12">
                  <PhysicalSetBlock
                    currentProduct={currentProduct}
                    data={unSavedSkuSet ? unSavedSkuSet : skuSet}
                    handleUpdateSkuSet={handleUpdateSkuSet}
                    handleChangeInput={handleChangeInput}
                    needAdditionalData={isNew}
                    isNew={isNew}
                  />
                </div>
              </div>
            ) : null}
            {values.type === 'BUNDLE' ? (
              <div className="row">
                <div className="col-12">
                  <VirtualSetBlock
                    currentProduct={currentProduct}
                    data={isNew ? unSavedSkuSet : skuSet}
                    handleUpdateSkuSet={handleUpdateSkuSet}
                    handleChangeInput={handleChangeInput}
                    needAdditionalData={isNew}
                    isNew={isNew}
                    openItem={openItem}
                  />
                </div>
              </div>
            ) : null}

            {isSingleProduct && !isHaveOptionsUsed ? (
              <div className="card-box">
                <MarginSalesBlock
                  values={values}
                  handleChangeInput={handleChangeInput}
                  productId={productId}
                  variantId={currentProduct?.variants?.[0]?.id}
                />
              </div>
            ) : null}

            {isSingleProduct && !isHaveOptionsUsed && haveProductsPriceMarginHistory ? (
              <ProductsPriceMarginHistory productsPriceMarginHistory={productsPriceMarginHistory} />
            ) : null}

            {isSingleProduct &&
            !isHaveOptionsUsed &&
            (productMarginShipped || productMarginDelivered) ? (
              <div className="row">
                <MarginSalesChart
                  title={t('Маржинальность продаж')}
                  shipped={productMarginShipped}
                  delivered={productMarginDelivered}
                />
              </div>
            ) : null}

            {values.type === 'SKU' ? (
              <>
                <VariantsBlock
                  loading={loading}
                  productId={productId}
                  isSavedVariants={isSavedVariants}
                  needSaving={needSaving}
                  features={features}
                  variants={variants}
                  variantsValidation={variantsValidation}
                  selectedVariants={selectedVariants}
                  allSelectedVariants={allSelectedVariants}
                  appearanceFields={appearanceFields}
                  handleSelectVariant={handleSelectVariant}
                  handleAllSelectVariants={handleAllSelectVariants}
                  options={options}
                  updateVariant={updateVariant}
                  deleteVariant={deleteVariant}
                  deleteLoading={deleteLoading}
                  setOptions={(options) => dispatch({ type: 'setOptions', options })}
                  useVariants={useVariants}
                  addNewFeature={addNewFeature}
                  handleVariantsUsing={handleVariantsUsing}
                  handleGenerateVariants={() =>
                    dispatch({ type: 'updateGenerateVariantsStatus', needToGenerateVariants: true })
                  }
                  handleNeedSaving={handleNeedSaving}
                  handleSetLastAction={handleSetLastAction}
                  handleAddManualVariant={handleAddManualVariant}
                  setUnsavedStatus={setUnsavedStatus}
                  openVariant={openVariant}
                  refreshArrearance={refreshArrearance}
                  moveVariant={moveVariant}
                  dispatch={dispatch}
                  actualizePhysicalSet={actualizePhysicalSet}
                />
              </>
            ) : null}
          </>
        ) : null}
        {currentProduct && !useVariants ? (
          <MonoboxesBlock
            product={currentProduct}
            variant={false} // Single Product
            setUnsavedStatus={setUnsavedStatus}
            monoboxesUpdateRef={monoboxesUpdateRef}
            refreshVariant={refreshProduct}
          />
        ) : null}
        {values.type !== 'SUBSCRIPTION' && values.type !== 'AD_ATTACHMENT' && !isNew ? (
          <ProductChangesBlock productId={productId} loading={loading} />
        ) : null}
        {values.type == 'SUBSCRIPTION' ? (
          <div className="row">
            <div className="col-lg-6">
              <PriceBlock
                currentProduct={currentProduct}
                values={values}
                handleChangeInput={handleChangeInput}
              />
            </div>
            <ParametersBlock
              currentProduct={currentProduct}
              values={values}
              warehouses={warehouses}
              errors={errors}
              handleChangeInput={handleChangeInput}
              handleChangeWeightType={handleChangeWeightType}
              handleSubmit={handleSubmit}
            />
            <div className="col-12">
              <SubscriptionBlock
                isNew={isNew}
                currentProduct={currentProduct}
                saveProduct={saveProduct}
              />
            </div>
          </div>
        ) : null}

        {values.type == 'AD_ATTACHMENT' ? (
          <div className="row">
            <AdAttachmentsParametersBlock
              currentProduct={currentProduct}
              values={values}
              warehouses={warehouses}
              errors={errors}
              handleChangeInput={handleChangeInput}
              handleChangeWeightType={handleChangeWeightType}
              handleSubmit={handleSubmit}
            />
            <ParametersBlock
              currentProduct={currentProduct}
              values={values}
              warehouses={warehouses}
              errors={errors}
              handleChangeInput={handleChangeInput}
              handleChangeWeightType={handleChangeWeightType}
              handleSubmit={handleSubmit}
              type={values.type}
              eshops={eshops}
            />
          </div>
        ) : null}

        <div className="row">
          <div className="col-12" style={{ display: 'flex' }}>
            <Button
              type="submit"
              text={t('Сохранить')}
              disabled={loading || !allIsValid}
              loading={loading === 'main'}
            />
            {!isNew && currentProduct ? (
              <Fragment>
                {!isArchived ? (
                  <Fragment>
                    <Button
                      type="button"
                      text={t('Копия')}
                      disabled={loading}
                      loading={false}
                      icon="fe-copy"
                      classType="btn-secondary"
                      className="ml-2"
                      onClick={handleCopy}
                    />
                    {isSingleProduct ? (
                      <Button
                        type="button"
                        text={!currentProduct.available ? t('Отобразить') : t('Скрыть')}
                        disabled={loading}
                        loading={loading === 'available'}
                        icon={!currentProduct.available ? 'fe-eye' : 'fe-eye-off'}
                        classType="btn-secondary"
                        className="ml-2"
                        onClick={handleHideShow}
                      />
                    ) : null}
                  </Fragment>
                ) : null}

                <div style={{ position: `relative` }} className="ml-2">
                  <button
                    type="button"
                    className="btn btn-secondary dropdown-toggle"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    <>
                      {t('Действия')}
                      <i className="mdi mdi-chevron-up" />
                    </>
                  </button>
                  <div
                    className={`dropdown-menu ${showDropdown ? `show` : ``}`}
                    onClick={() => setShowDropdown(false)}
                  >
                    {!useVariants ? (
                      <>
                        <div
                          className="dropdown-item"
                          onClick={() => handleExportMovements(currentProduct.variants[0].id)}
                        >
                          <i className="fe-arrow-down mr-2" />
                          {t('Отчёт о движениях')}
                        </div>
                        <div className="dropdown-item" onClick={() => handleShowStockByBatch(true)}>
                          <i className="fe-arrow-down mr-2" />
                          {t('Отчет об остатках')}
                        </div>
                        {currentProduct?.type == 'SKU' ||
                        (!skuSet && currentProduct?.type == 'PHYSICAL_SET') ? (
                          <div
                            className="dropdown-item"
                            onClick={() => handleCreateSkuSet('PHYSICAL_SET')}
                          >
                            {' '}
                            <i className="fe-box mr-2" />
                            {t('Указать состав комплекта')}
                          </div>
                        ) : null}
                        {currentProduct?.type == 'SKU' ||
                        (!skuSet && currentProduct?.type == 'BUNDLE') ? (
                          <div
                            className="dropdown-item"
                            onClick={() => handleCreateSkuSet('BUNDLE')}
                          >
                            {' '}
                            <i className="fe-inbox mr-2" />
                            {t('Указать состав виртуального набора')}
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    <div className="dropdown-item" onClick={() => openImportChestnyznakModal()}>
                      <i className="fe-arrow-up mr-2" />
                      {t('Импорт пула номеров «Честный Знак»')}
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  text={!currentProduct.deleted ? t('В архив') : t('Восстановить')}
                  disabled={loading}
                  loading={loading === 'archive'}
                  icon="fe-archive"
                  classType="btn-danger"
                  className="ml-auto"
                  onClick={handleArchiveItem}
                />
              </Fragment>
            ) : null}
          </div>
        </div>
      </FormWrapper>
    </>
  );
};

export default DesktopLayout;
